// File: src/components/AltitudeGauge/AltitudeGauge.js
import React, { useEffect, useState } from 'react';
import useActiveSection, { SECTION_ORDER } from '../../hooks/useActiveSection';
import { subscribe } from '../../hooks/useScrollProgress';
import RocketIcon from '../icons/RocketIcon';

// Top-to-bottom DOM/page order (contact is first in the DOM / top of the
// page, home is last / bottom of the page) — derived from the narrative
// SECTION_ORDER rather than hand-written, so the two never drift apart.
const DOT_ORDER = SECTION_ORDER.slice().reverse();

const dotPercent = (id) => {
  const index = DOT_ORDER.indexOf(id);
  return (index / (DOT_ORDER.length - 1)) * 100;
};

/**
 * Slim right-docked "altitude gauge" that replaces both the old floating
 * rocket (App.js) and the old left-docked pill nav. The rocket
 * now doubles as a scroll-progress position marker and the "advance to
 * next section" control; a column of dots next to it offers direct-jump
 * navigation to any section. Both share one source of truth for "current
 * section" (useActiveSection) instead of maintaining separate state.
 */
const AltitudeGauge = () => {
  const activeId = useActiveSection();
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollFraction, setScrollFraction] = useState(0);
  // Lazy initializer runs synchronously during the first render, so this
  // is correct from the very first paint - unlike a ref set inside a
  // useEffect, which would only update after mount and wouldn't trigger a
  // re-render anyway (the previous version of this read a ref during
  // render, so it was always stale/false on first paint and never
  // re-evaluated).
  const [isReducedMotion] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    if (isReducedMotion) {
      // Don't continuously track scroll position; the rocket stays parked
      // near the current active section (handled by rocketTop below,
      // which falls back to dotPercent(activeId)).
      return undefined;
    }

    const updateFraction = () => {
      // Measure against <main> rather than the whole document - the page
      // also has a trailing <footer>, and including its height in the
      // denominator meant the rocket never quite reached 100% even when
      // scrolled all the way to the last section.
      const main = document.getElementById('main-content');
      const scrollableHeight = main
        ? main.offsetHeight - window.innerHeight
        : document.documentElement.scrollHeight - window.innerHeight;
      const fraction = scrollableHeight > 0 ? window.scrollY / scrollableHeight : 0;
      setScrollFraction(Math.min(1, Math.max(0, fraction)));
    };

    // Register through the shared scroll-listener singleton instead of
    // adding a second native `scroll` listener.
    const unsubscribe = subscribe(updateFraction);
    updateFraction(); // Initial call, matching the pattern in useScrollProgress.

    return unsubscribe;
  }, [isReducedMotion]);

  // Advance to the next section in narrative order. Preserves the
  // mobile-vs-desktop scroll implementation (custom rAF easing tween on
  // mobile, native scrollIntoView on desktop) and the isScrolling debounce
  // guard from the old App.js scrollBetweenSections — only how the target
  // section is determined has been simplified (previously: scan all
  // sections for max visible area, with a fallback counter cycling
  // sections on non-scrollable pages; now: SECTION_ORDER.indexOf(activeId)
  // + 1).
  const scrollBetweenSections = () => {
    if (isScrolling) {
      return;
    }

    setIsScrolling(true);

    const safetyTimeout = setTimeout(() => {
      setIsScrolling(false);
    }, 2000);

    const isMobileWidth = window.innerWidth <= 768;
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isMobile = isMobileWidth || isMobileDevice || isTouchDevice;

    const smoothScrollToSection = (targetSection) => {
      if (!targetSection) {
        setIsScrolling(false);
        return;
      }

      const fallbackScroll = () => {
        clearTimeout(safetyTimeout);
        targetSection.scrollIntoView({
          behavior: isMobile ? 'auto' : 'smooth',
          block: 'start',
          inline: 'nearest'
        });
        setTimeout(() => setIsScrolling(false), isMobile ? 300 : 1000);
      };

      if (isMobile) {
        const startY = window.scrollY;
        const targetY = targetSection.offsetTop;
        const distance = targetY - startY;
        const duration = Math.min(1200, Math.max(600, Math.abs(distance) / 1.5));
        const startTime = performance.now();

        document.documentElement.style.scrollBehavior = 'auto';
        document.body.style.scrollBehavior = 'auto';

        const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1);

        const scroll = (currentTime) => {
          try {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = easeInOutCubic(progress);
            const currentY = startY + distance * easedProgress;

            window.scrollTo(0, currentY);

            if (progress < 1) {
              requestAnimationFrame(scroll);
            } else {
              clearTimeout(safetyTimeout);
              setTimeout(() => {
                document.documentElement.style.scrollBehavior = '';
                document.body.style.scrollBehavior = '';
                setIsScrolling(false);
              }, 100);
            }
          } catch (error) {
            fallbackScroll();
          }
        };

        try {
          requestAnimationFrame(scroll);
        } catch (error) {
          fallbackScroll();
        }
      } else {
        try {
          targetSection.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
          clearTimeout(safetyTimeout);
          setTimeout(() => setIsScrolling(false), 1000);
        } catch (error) {
          fallbackScroll();
        }
      }
    };

    const currentIndex = SECTION_ORDER.indexOf(activeId);
    const nextIndex = (currentIndex === -1 ? 0 : currentIndex + 1) % SECTION_ORDER.length;
    const targetSection = document.getElementById(SECTION_ORDER[nextIndex]);

    smoothScrollToSection(targetSection);
  };

  const goToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const rocketTop = isReducedMotion ? dotPercent(activeId) : scrollFraction * 100;

  return (
    <nav
      aria-label="Section navigation"
      className="fixed right-3 sm:right-6 top-1/2 -translate-y-1/2 z-50 flex flex-col items-center gap-1"
    >
      <div className="relative h-56 sm:h-72 w-6">
        {/* Decorative vertical line */}
        <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-white/20" />

        {/* Direct-jump section dots, top-to-bottom in DOM order */}
        {DOT_ORDER.map((id) => {
          const isActive = id === activeId;
          const label = id.charAt(0).toUpperCase() + id.slice(1);

          return (
            <button
              key={id}
              type="button"
              onClick={() => goToSection(id)}
              aria-label={`Go to ${label} section`}
              aria-current={isActive ? 'true' : undefined}
              title={label}
              className="group absolute left-1/2 w-8 h-8 flex items-center justify-center border-0 p-0 bg-transparent"
              style={{ top: `${dotPercent(id)}%`, transform: 'translate(-50%, -50%)' }}
            >
              <span
                aria-hidden="true"
                className={`block w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  isActive
                    ? 'bg-space-blue-400 ring-2 ring-space-blue-400/40 scale-125'
                    : 'bg-white/30 group-hover:bg-white/50'
                }`}
              />
            </button>
          );
        })}

        {/* Rocket: scroll-progress position marker + advance-to-next-section control */}
        <button
          type="button"
          onClick={scrollBetweenSections}
          aria-label={isScrolling ? 'Traveling...' : 'Explore the Universe — go to next section'}
          className="absolute border-0 p-0 bg-transparent"
          style={{
            top: `${rocketTop}%`,
            // Anchored to the left of the dot track (growing toward the
            // page's center) rather than the right (growing off the edge
            // of the viewport): the rocket glyph itself is 50-60px wide
            // via the .rocket CSS class, which the track's own width and
            // a small margin didn't account for, so it was rendering
            // 70-85% clipped off-screen.
            right: '100%',
            marginRight: '10px',
            transform: 'translateY(-50%)',
            transition: isReducedMotion ? 'none' : 'top 0.15s linear',
            WebkitTouchCallout: 'none',
            WebkitUserSelect: 'none',
            userSelect: 'none',
            touchAction: 'manipulation'
          }}
        >
          <RocketIcon
            className={`rocket ${isScrolling ? 'scrolling' : ''}`}
            style={{
              opacity: isScrolling ? 0.6 : 1,
              cursor: isScrolling ? 'wait' : 'pointer'
            }}
          />
        </button>
      </div>
    </nav>
  );
};

export default AltitudeGauge;
